import { useState, useEffect, useCallback } from 'react'

/**
 * Hook to detect online/offline status
 * Uses the browser's navigator.onLine API with event listeners
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => {
    // SSR-safe initial value
    if (typeof navigator !== 'undefined') {
      return navigator.onLine
    }
    return true
  })

  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      // Track that we were offline so we can show "reconnected" message
      if (!isOnline) {
        setWasOffline(true)
        // Clear the wasOffline flag after a few seconds
        setTimeout(() => setWasOffline(false), 5000)
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [isOnline])

  // Helper function to check if an action can proceed
  const checkConnection = useCallback((showToast?: (message: string) => void): boolean => {
    if (!isOnline) {
      showToast?.('You are offline. Please check your internet connection.')
      return false
    }
    return true
  }, [isOnline])

  return {
    isOnline,
    isOffline: !isOnline,
    wasOffline,
    checkConnection,
  }
}

export default useOnlineStatus
